<template>
  <!-- DP-5377 (Canada): Add Tracking, Add Another Shipment, Edit Tracking and
       Resend Email for Canada Post tracking URLs entered by hand. Opened with
       $root.$emit('openCaShipment', { mode, orderId, shipment }). -->
  <div>
    <b-modal id="ca-shipment-modal" size="lg" footer-class="mt-4" @hidden="reset">
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">{{ title }}</h3>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <b-container class="px-5">
        <div v-if="loading" class="dp-ca-muted">
          <b-spinner small /><span class="ml-2">Loading…</span>
        </div>

        <template v-else-if="overview">
          <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

          <template v-if="mode === 'resend'">
            <p class="mb-2">
              Send the <b>{{ kindLabel }}</b> email for <b>{{ shipmentTitle }}</b> to the customer?
            </p>
            <div class="dp-ca-field">
              <div class="dp-ca-label">Tracking URL</div>
              <div class="dp-ca-url">{{ shipment.tracking_url }}</div>
            </div>
          </template>

          <template v-else>
            <b-form-group v-if="mode === 'edit'" label="Current Tracking URL">
              <b-form-input :value="shipment.tracking_url" readonly />
            </b-form-group>

            <b-form-group
              :label="mode === 'first' ? 'Tracking URL *' : 'New Tracking URL *'"
              :state="urlState"
              :invalid-feedback="urlFeedback"
            >
              <b-form-input
                v-model.trim="form.trackingUrl"
                :state="urlState"
                placeholder="https://www.canadapost-postescanada.ca/track-reperage/en#/details/…"
              />
            </b-form-group>

            <b-form-group
              v-if="mode !== 'edit'"
              :label="mode === 'first' ? 'Shipment Reason' : 'Shipment Reason *'"
            >
              <b-form-select
                v-model="form.reason"
                :options="reasonOptions"
                :disabled="mode === 'first'"
              />
            </b-form-group>

            <b-form-group
              v-if="form.reason === 'other' && mode === 'another'"
              label="Explain the reason *"
            >
              <b-form-input v-model.trim="form.reasonOther" maxlength="255" />
            </b-form-group>

            <b-form-group v-if="mode !== 'first'" label="Internal Notes">
              <b-form-textarea v-model="form.notes" rows="2" maxlength="2000" />
            </b-form-group>

            <b-form-checkbox v-model="form.sendEmail" class="dp-ca-checkbox mb-2">{{
              emailCheckboxLabel
            }}</b-form-checkbox>
          </template>

          <div v-if="willEmail" class="dp-ca-recipient">
            <span>
              Email to <b>{{ recipient || 'no email on this order' }}</b>
              <span class="dp-ca-muted ml-1">· {{ kindLabel }}</span>
            </span>
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="previewDisabled"
              @click="openPreview"
            >
              Preview Email
            </b-button>
          </div>

          <!-- QA tools: only when api-v4 runs with SHIPMENT_EMAIL_TEST_TOOLS=true
             (branch boxes, never production). The API ignores these fields
             otherwise. -->
          <div v-if="overview.test_tools && willEmail" class="dp-ca-test-tools">
            <div class="dp-ca-test-title">QA test tools (test environment only)</div>
            <b-form-group label="Send the email to this address instead" class="mb-2">
              <b-form-input
                v-model.trim="form.testRecipient"
                type="email"
                placeholder="you@dresden.vision"
              />
            </b-form-group>
            <b-form-checkbox v-model="form.testFail" class="dp-ca-checkbox">
              Simulate email failure
            </b-form-checkbox>
          </div>
        </template>
      </b-container>

      <template v-slot:modal-footer="{ cancel }">
        <b-container>
          <div class="d-flex justify-content-end px-4">
            <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
            <b-button
              variant="primary"
              class="ml-3"
              :disabled="saving || loading || !overview"
              @click="submit"
            >
              <b-spinner v-if="saving" small class="mr-2" />{{ submitLabel }}
            </b-button>
          </div>
        </b-container>
      </template>
    </b-modal>

    <b-modal id="ca-shipment-email-preview" size="xl" ok-only ok-title="Close">
      <template v-slot:modal-header="{ close }">
        <b-container>
          <div class="modal-title">
            <h3 class="text-bold text-center">Email Preview</h3>
            <button type="button" aria-label="Close" class="close" @click="close">
              <i class="di-remove-10"></i>
            </button>
          </div>
        </b-container>
      </template>
      <b-container class="px-5">
        <div v-if="previewLoading" class="dp-ca-muted">
          <b-spinner small /><span class="ml-2">Loading preview…</span>
        </div>
        <b-alert v-else-if="previewError" show variant="danger">{{ previewError }}</b-alert>
        <template v-else-if="preview">
          <div class="dp-ca-field"><span class="dp-ca-label">To</span> {{ recipient }}</div>
          <div class="dp-ca-field">
            <span class="dp-ca-label">Subject</span> {{ preview.subject }}
          </div>
          <div class="dp-ca-field">
            <span class="dp-ca-label">Tracking URL</span> {{ previewTrackingUrl }}
          </div>
          <iframe class="dp-ca-preview" :srcdoc="preview.html" sandbox="" title="Email preview" />
        </template>
      </b-container>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import apiOrders from '@/api/orders';

const URL_PATTERN = /^(https?:\/\/)?[a-z0-9]+([-.][a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i;

const TITLES = {
  first: 'Add Tracking',
  another: 'Add Another Shipment',
  edit: 'Edit Tracking',
  resend: 'Resend Email',
};

const SUBMIT_LABELS = {
  first: 'Save Tracking',
  another: 'Save Shipment',
  edit: 'Save Changes',
  resend: 'Send Email',
};

const KIND_LABELS = {
  initial: 'Order Is Dispatched',
  additional: 'Additional Shipment',
  updated: 'Updated Tracking',
};

export default {
  name: 'CaShipmentModal',
  data() {
    return {
      mode: 'first',
      orderId: null,
      shipment: null,
      overview: null,
      loading: false,
      saving: false,
      error: null,
      submitted: false,
      form: this.emptyForm(),
      preview: null,
      previewLoading: false,
      previewError: null,
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.auth.user,
    }),
    title() {
      return TITLES[this.mode];
    },
    submitLabel() {
      return SUBMIT_LABELS[this.mode];
    },
    shipmentTitle() {
      return this.shipment ? `Shipment #${this.shipment.number}` : '';
    },
    emailKind() {
      if (this.mode === 'first') return 'initial';
      if (this.mode === 'another') return 'additional';
      if (this.mode === 'edit') return 'updated';

      return this.shipment && this.shipment.number === 1 ? 'initial' : 'additional';
    },
    kindLabel() {
      return KIND_LABELS[this.emailKind];
    },
    emailCheckboxLabel() {
      if (this.mode === 'first') return 'Send "Order Is Dispatched" email to customer';
      if (this.mode === 'edit') return 'Send updated tracking email to customer';

      return 'Send shipment notification email to customer';
    },
    willEmail() {
      return this.mode === 'resend' || this.form.sendEmail;
    },
    recipient() {
      return (
        (this.overview && this.overview.test_tools && this.form.testRecipient) ||
        this._.get(this.overview, 'email_recipient')
      );
    },
    reasonOptions() {
      const reasons = this._.get(this.overview, 'shipment_reasons', {});

      return Object.keys(reasons)
        .filter(key =>
          this.mode === 'first' ? key === 'initial_shipment' : key !== 'initial_shipment',
        )
        .map(key => ({ value: key, text: reasons[key] }));
    },
    urlValid() {
      return URL_PATTERN.test(this.form.trackingUrl);
    },
    urlState() {
      if (!this.submitted) return null;

      return this.urlValid ? null : false;
    },
    urlFeedback() {
      return this.form.trackingUrl
        ? 'Enter a valid tracking URL.'
        : 'The tracking URL is required.';
    },
    previewTrackingUrl() {
      return this.mode === 'resend' ? this.shipment.tracking_url : this.form.trackingUrl;
    },
    previewDisabled() {
      return this.mode !== 'resend' && !this.urlValid;
    },
  },
  watch: {
    // DP-5598: a "Choose a shipment reason." / "Explain the reason." message
    // clears as soon as staff change the form.
    form: {
      deep: true,
      handler() {
        if (this.error && !this.saving) this.error = null;
      },
    },
  },
  created() {
    this.$root.$on('openCaShipment', this.open);
  },
  beforeDestroy() {
    this.$root.$off('openCaShipment', this.open);
  },
  methods: {
    emptyForm() {
      return {
        trackingUrl: '',
        reason: null,
        reasonOther: '',
        notes: '',
        sendEmail: false,
        testRecipient: '',
        testFail: false,
      };
    },
    async open({ mode, orderId, shipment = null }) {
      this.mode = mode;
      this.orderId = orderId;
      this.shipment = shipment;
      this.form = {
        ...this.emptyForm(),
        reason: mode === 'first' ? 'initial_shipment' : null,
        sendEmail: mode === 'first',
      };
      this.$bvModal.show('ca-shipment-modal');

      this.loading = true;
      try {
        const { data } = await apiOrders.getOrderShipments(orderId);
        this.overview = data;
      } catch (error) {
        this.error = 'The shipping details could not be loaded. Close and try again.';
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.overview = null;
      this.error = null;
      this.submitted = false;
      this.saving = false;
    },
    testFields() {
      if (!this.overview.test_tools) return {};

      return {
        test_recipient: this.form.testRecipient || null,
        test_fail_email: this.form.testFail,
      };
    },
    validate() {
      this.submitted = true;
      if (this.mode === 'resend') return true;
      if (!this.urlValid) return false;
      if (this.mode === 'another' && !this.form.reason) {
        this.error = 'Choose a shipment reason.';
        return false;
      }
      if (this.mode === 'another' && this.form.reason === 'other' && !this.form.reasonOther) {
        this.error = 'Explain the reason.';
        return false;
      }

      return true;
    },
    request() {
      const email = this.willEmail ? this.testFields() : {};

      if (this.mode === 'resend') {
        return apiOrders.sendShipmentEmail(this.shipment.id, email);
      }
      if (this.mode === 'edit') {
        return apiOrders.updateShipmentTracking(this.shipment.id, {
          tracking_id: this.form.trackingUrl,
          internal_notes: this.form.notes || null,
          send_email: this.form.sendEmail,
          ...email,
        });
      }

      return apiOrders.getQuoteManuelEntry({
        order_id: this.orderId,
        sender_outlet_id: this._.get(this.currentUser, 'outlet.id'),
        tracking_id: this.form.trackingUrl,
        shipment_reason: this.form.reason,
        shipment_reason_other: this.form.reason === 'other' ? this.form.reasonOther : null,
        internal_notes: this.form.notes || null,
        send_email: this.form.sendEmail,
        ...email,
      });
    },
    async submit() {
      this.error = null;
      if (!this.validate()) return;

      this.saving = true;
      try {
        await this.request();
        // DP-5599: the list is reloaded before "saved" is shown, so the new
        // shipment is already there when staff read the message.
        const overview = await apiOrders
          .getOrderShipments(this.orderId)
          .then(response => response.data)
          .catch(() => null);
        this.$root.$emit('shipmentsChanged', this.orderId, overview);
        this.$store.dispatch('showSuccessAlert', this.successMessage());
        this.$bvModal.hide('ca-shipment-modal');
        this.$root.$emit('reloadExpandedJob', this.orderId);
      } catch (error) {
        const errors = this._.get(error, 'response.data.errors');
        this.error = errors
          ? Object.values(errors).flat().join(' ')
          : this._.get(error, 'response.data.message') || 'Something went wrong. Please try again.';
      } finally {
        this.saving = false;
      }
    },
    successMessage() {
      const emailed = this.willEmail ? ' The email is on its way.' : '';
      if (this.mode === 'resend') return 'The email is on its way.';
      if (this.mode === 'edit') return `Tracking URL updated.${emailed}`;

      return `Tracking URL saved.${emailed}`;
    },
    async openPreview() {
      this.preview = null;
      this.previewError = null;
      this.previewLoading = true;
      this.$bvModal.show('ca-shipment-email-preview');
      try {
        const params = { order_id: this.orderId, email_kind: this.emailKind };
        if (this.mode === 'resend') {
          params.order_shipment_id = this.shipment.id;
        } else {
          params.tracking_url = this.form.trackingUrl;
        }
        const { data } = await apiOrders.previewShipmentEmail(params);
        this.preview = data;
      } catch (error) {
        this.previewError = 'The preview could not be loaded.';
      } finally {
        this.previewLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.dp-ca-muted {
  color: #718098;
  font-size: 13px;
}
.dp-ca-field {
  font-size: 14px;
  margin-bottom: 8px;
}
.dp-ca-label {
  color: #a9aaab;
  font-size: 12px;
  margin-right: 6px;
}
.dp-ca-url {
  word-break: break-all;
}
.dp-ca-recipient {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fafafa;
  border: 1px solid #f4f4f5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 8px;
  font-size: 14px;
}
.dp-ca-test-tools {
  border: 1px dashed #f0ad4e;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 12px;
  background: #fffaf2;
}
.dp-ca-test-title {
  font-size: 12px;
  font-weight: 700;
  color: #b06d00;
  margin-bottom: 8px;
  text-transform: uppercase;
}
.dp-ca-preview {
  width: 100%;
  height: 70vh;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  margin-top: 8px;
}
.dp-ca-checkbox >>> .custom-control-label::before,
.dp-ca-checkbox >>> .custom-control-label::after {
  top: 0;
}
.dp-ca-checkbox >>> .custom-control-label {
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
}
</style>
